import { z } from 'zod';
import type { CustomerTierToken } from '@sleed/bizcommerce-api';

/**
 * Customer tier edit form. Mirrors `CustomerTierToken` and the legacy admin grid
 * (Core/client/admin/src/pages/Customers/CustomerTiers/index.js): nm required ≤100, cd required ≤50,
 * descr optional ≤500, lvl required, multiplier optional, chex optional hex color, iv/act booleans,
 * si required. The ML sub-token (btrl), erpcd and timestamps (cdt/udt) are not surfaced by the legacy
 * grid; erpcd is carried through unchanged (hidden) so an edit never wipes it, and the default-language
 * name/descr are saved at the top level via the Accept-Language=el pin (parity with RelationTypes).
 */
export const customerTierSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	cd: z.string().trim().min(1, 'validation.required').max(50, 'validation.tooLong'),
	descr: z.string().trim().max(500, 'validation.tooLong').nullable(),
	lvl: z.number({ message: 'validation.required' }).int(),
	multiplier: z.number().nullable(),
	// Euro purchase amount at which the tier starts (storefront tier profile, BW-8339)
	paf: z.number().min(0).nullable(),
	chex: z.string().trim().max(20, 'validation.tooLong').nullable(),
	iv: z.boolean(),
	act: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	// Carried through unchanged — not surfaced in the legacy tiers grid.
	erpcd: z.string().nullable().optional()
});

export type CustomerTierFormValues = z.infer<typeof customerTierSchema>;

export const EMPTY_CUSTOMER_TIER: CustomerTierFormValues = {
	nm: '',
	cd: '',
	descr: '',
	lvl: 0,
	multiplier: null,
	paf: null,
	chex: null,
	iv: true,
	act: true,
	si: 0,
	erpcd: null
};

export function tokenToForm(token: CustomerTierToken): CustomerTierFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		cd: token.cd ?? '',
		descr: token.descr ?? '',
		lvl: token.lvl ?? 0,
		multiplier: token.multiplier ?? null,
		paf: token.paf ?? null,
		chex: token.chex ?? null,
		iv: token.iv ?? false,
		act: token.act ?? false,
		si: token.si ?? 0,
		erpcd: token.erpcd ?? null
	};
}

export function formToToken(form: CustomerTierFormValues): CustomerTierToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		cd: form.cd.trim(),
		descr: form.descr?.trim() || null,
		lvl: form.lvl,
		multiplier: form.multiplier ?? null,
		paf: form.paf ?? null,
		chex: form.chex?.trim() || null,
		iv: form.iv,
		act: form.act,
		si: form.si,
		erpcd: form.erpcd ?? null
	};
}
